import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Swal from 'sweetalert2';
import { Dropdown } from 'react-bootstrap';

import { UseOutletContextPayroll } from './Payroll';
import EmployeeFormFields from './EmployeeFormFields';
import { StateLanguage } from '../../../languages/config/StateLanguage';
import { EmployeeDA } from '../../../services/admin/employee.service';
import { ApiDigitalAdminContact } from '../../../services/admin/contact.service';
import { employeeSchema, EmployeeFormData } from '../../../schemas/employee.schema';
import { configureZodErrors } from '../../../schemas/zod-error-map';
import { Contact } from '../../../models/contact.model';
import { formatContact } from '../../../scripts/format.value.script';

const EmployeeCreatePage: React.FunctionComponent = () => {
  const { setRoute, companyForUser } = UseOutletContextPayroll();
  const { lang } = StateLanguage();
  const navigate = useNavigate();

  const [submitting, setSubmitting] = useState(false);
  const [contactSearch, setContactSearch] = useState('');
  const [contactResults, setContactResults] = useState<Contact[]>([]);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    setRoute({path: {root: lang.labels.payroll, branch: lang.labels.addEmployee}, company: false})
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  configureZodErrors(lang.labels);

  const { control, handleSubmit, watch, setValue, formState: { errors } } = useForm<EmployeeFormData>({
    resolver: zodResolver(employeeSchema),
    mode: 'onBlur',
    defaultValues: {
      id_contact: '',
      contact_snapshot: { name: '', address: {}, language: 'fr' },
      avs_number: '',
      birth_date: '',
      gender: undefined,
      civil_status: undefined,
      civil_status_since: '',
      nationality: '',
      permit: { type: 'CH', number: '', valid_until: '', issuing_canton: '' },
      residence: { canton: '', municipality: '', municipality_bfs: '', country: 'CH' },
      work_canton: '',
      spouse: { has_spouse: false, is_working: false, work_canton: '', work_country: '' },
      children: [],
      payment: { iban: '', bic: '', holder_name: '' },
      notes: '',
    },
  });

  const searchContacts = useCallback(async (query: string) => {
    if (!companyForUser || query.length < 2) {
      setContactResults([]);
      return;
    }
    setSearching(true);
    const response = await ApiDigitalAdminContact.getContacts(
      companyForUser.id, query, '', 'person', 'name', 1, 10
    );
    if (response.status === 200 && response.data) {
      const items = response.data.items ?? response.data ?? [];
      setContactResults(Array.isArray(items) ? items : []);
    } else {
      setContactResults([]);
    }
    setSearching(false);
  }, [companyForUser]);

  const handleContactSelect = (contact: Contact) => {
    setSelectedContact(contact);
    setContactSearch('');
    setContactResults([]);

    setValue('id_contact', contact.id);

    const d = contact.data;
    const displayName = d.first_name && d.last_name
      ? `${d.first_name} ${d.last_name}`
      : d.name || '';

    setValue('contact_snapshot', {
      name: displayName,
      first_name: d.first_name || null,
      last_name: d.last_name || null,
      address: {
        street: d.address?.street || '',
        number: d.address?.number || '',
        complement: d.address?.additional || null,
        zip: d.address?.zip_code || '',
        city: d.address?.city || '',
        country: d.address?.country || 'CH',
      },
      email: d.email || null,
      phone: d.phone || null,
      language: d.language || 'fr',
    });
  };

  const clearContact = () => {
    setSelectedContact(null);
    setValue('id_contact', '');
    setValue('contact_snapshot', { name: '', address: {}, language: 'fr' });
  };

  const onSubmit = async (data: any) => {
    if (!companyForUser) return;
    setSubmitting(true);

    const response = await EmployeeDA.postEmployee(companyForUser.id, data);

    if (response.status === 201 || response.status === 200) {
      Swal.fire({
        title: lang.labels.successfullyCreated || 'Created successfully',
        icon: 'success',
        showConfirmButton: false,
        timer: 1500,
      });
      const employeeId = response.data?.id;
      navigate(employeeId ? `/main/payroll/employees/view/${employeeId}` : '/main/payroll/employees/list');
    } else {
      Swal.fire({
        title: lang.labels.error || 'Error',
        text: response.data?.message || response.message || 'An error occurred',
        icon: 'error',
      });
    }

    setSubmitting(false);
  };

  return (
    <div className="card">
      <div className="card-header">
        <div className="card-title">
          <h3 className="fw-bolder">{lang.labels.addEmployee}</h3>
        </div>
      </div>
      <div className="card-body">
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          {/* Contact search — select2-styled dropdown with remote search */}
          <div className="fv-row mb-5">
            <label className="form-label required">{lang.labels.linkedContact || 'Linked contact'}</label>
            {selectedContact ? (
              <div className="d-flex align-items-center bg-light-primary rounded p-4">
                <div className="flex-grow-1">
                  <span className="fw-bold fs-6">
                    {formatContact(selectedContact)}
                  </span>
                  {selectedContact.data?.email && (
                    <span className="text-muted ms-2">({selectedContact.data.email})</span>
                  )}
                </div>
                <button type="button" className="btn btn-sm btn-light" onClick={clearContact}>
                  {lang.labels.change || 'Change'}
                </button>
              </div>
            ) : (
              <Dropdown show={contactResults.length > 0}>
                <div className="position-relative">
                  <input
                    className={`form-control ${errors.id_contact ? 'is-invalid' : ''}`}
                    type="text"
                    autoComplete="off"
                    placeholder={lang.labels.searchContact || 'Search for an existing contact...'}
                    value={contactSearch}
                    onChange={(e) => {
                      setContactSearch(e.target.value);
                      searchContacts(e.target.value);
                    }}
                  />
                  {searching && (
                    <div className="position-absolute top-50 end-0 translate-middle-y me-3">
                      <span className="spinner-border spinner-border-sm text-muted"></span>
                    </div>
                  )}
                </div>
                <Dropdown.Menu variant="select2-container select2-container--bootstrap5 select2-container--open w-100" style={{ position: 'absolute', top: '100%', left: 0 }}>
                  <span className="select2-dropdown select2-dropdown--below">
                    <span className="select2-results">
                      <ul className="select2-results__options" role="listbox">
                        {contactResults.map(c => (
                          <li key={c.id} className="select2-results__option select2-results__option--selectable" role="option">
                            <Dropdown.Item bsPrefix="select2-results__option__text" onClick={() => handleContactSelect(c)}>
                              <span className="fw-bold">{formatContact(c)}</span>
                              {c.data?.email && <span className="text-muted ms-2 fs-7">{c.data.email}</span>}
                            </Dropdown.Item>
                          </li>
                        ))}
                      </ul>
                    </span>
                  </span>
                </Dropdown.Menu>
              </Dropdown>
            )}
            {errors.id_contact && !selectedContact && (
              <div className="fv-plugins-message-container invalid-feedback d-block">
                {lang.labels.contactRequired || 'Please select a contact'}
              </div>
            )}
          </div>

          <div className="separator my-5"></div>

          <EmployeeFormFields control={control} watch={watch} lang={lang} />

          <div className="separator my-5"></div>
          <div className="d-flex justify-content-end">
            <Link to="/main/payroll/employees/list" className="btn btn-light me-3">
              {lang.labels.cancel || 'Cancel'}
            </Link>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? (
                <span className="spinner-border spinner-border-sm me-2"></span>
              ) : null}
              {lang.labels.create || 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EmployeeCreatePage;
